<script lang="ts">
import "@cisco-u/modal/cu-modal.js";
import "@cisco-u/button/cu-button.js";

import { defineComponent } from "vue";
import { useCookies } from "vue3-cookies";
import { mapGetters } from "vuex";

import { ALLACCESS } from "@/consts/bundles";
import { Statsig } from "@/env/statsig/client";
import isUnauth from "@/utils/isUnauth";

const LTI_PLATFORM_COOKIE = "lti_platform";
const CSOD_DOMAIN_SUFFIX = ".csod.com";

const cookies = useCookies().cookies;

function getLtiPlatform(): string {
  return cookies.get(LTI_PLATFORM_COOKIE) || "";
}

export default defineComponent({
  name: "LtiProvisioningSplash",
  data() {
    return {
      ltiProvisioningDismissed: false,
      featureGateEnabled: false,
      ltiPlatform: getLtiPlatform(),
    };
  },
  computed: {
    ...mapGetters({
      status: "user/status",
      userEntitlements: "user/entitlements",
      freeId: "content/freeId",
    }),
    hasAllAccess(): boolean {
      return (this.userEntitlements ?? []).some(
        (entitlement: any) => entitlement?.content_bundle_name === ALLACCESS
      );
    },
    showSplash(): boolean {
      if (!this.featureGateEnabled) return false;
      if (this.ltiProvisioningDismissed) return false;
      if (isUnauth()) return false;
      if (!this.ltiPlatform.endsWith(CSOD_DOMAIN_SUFFIX)) return false;
      if (this.hasAllAccess) return false;
      return true;
    },
    exploreFreeHref(): string {
      const id = Number(this.freeId);
      if (!Number.isFinite(id) || id <= 0) return "/explore";
      return `/explore?subscription=${id}`;
    },
  },
    watch: {
    status: {
      handler(status) {
        if (status) {
          // Authenticated Statsig init and the lti_platform cookie can both be
          // set after initial render. Re-read them so showSplash recomputes
          // instead of being stuck on the early (pre-provisioning) values.
          this.ltiPlatform = getLtiPlatform();
          this.refreshFeatureGate();
        }
      },
      immediate: true,
    },
  },
  methods: {
    async refreshFeatureGate() {
      this.featureGateEnabled = await Statsig.featureGate("ENABLE_LTI_PROVISIONING_SPLASH");
    },
    dismiss() {
      this.ltiProvisioningDismissed = true;
    },
    onExploreClick(event: MouseEvent) {
      // Allow modifier / non-primary clicks to behave like normal anchors.
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        this.dismiss();
        return;
      }
      event.preventDefault();
      this.dismiss();
      this.$router.push(this.exploreFreeHref);
    },
  },
  created() {
    this.refreshFeatureGate();
  },
});
</script>

<template>
  <div
    v-if="showSplash"
    class="lti-provisioning-splash"
    data-cy="lti-provisioning-splash"
    @modalResponse="dismiss"
  >
    <cu-modal
      class="lti-provisioning-splash-modal"
      type="small"
      width="40rem"
      :open="showSplash"
      data-cy="lti-provisioning-splash-modal"
    >
      <div slot="header" class="px-6 pb-6 text-white-lightest">
        <div class="mb-6 text-center text-22 font-light">
          Cisco <strong class="font-thicker">U.</strong>
        </div>
        <h2
          class="text-center font-cisco text-28 font-light leading-tight"
          data-cy="lti-provisioning-splash-title"
        >
          Your Cisco U. learning is almost ready.
        </h2>
      </div>
      <div slot="body" class="px-6 py-4">
        <p class="text-16 leading-relaxed text-black-darkest">
          Watch for an email from no-reply@cisco.com to let you know when it's
          available. It should take 15 minutes or less.
        </p>
        <p class="mt-4 text-16 leading-relaxed text-black-darkest">
          Once you receive your Cisco U. email, return to Skills Studio to
          launch your learning for the first time.
        </p>
        <div class="mt-8 flex justify-center">
          <cu-button
            :href="exploreFreeHref"
            template="primary"
            data-cy="lti-provisioning-splash-cta"
            @click="onExploreClick"
          >
            Explore free training
          </cu-button>
        </div>
      </div>
    </cu-modal>
  </div>
</template>
